import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ListFilter } from 'lucide-react';
import { useFilterPanel } from './useFilterPanelPosition';
import './ColumnFilterDropdown.scss';

interface ColumnFilterOption {
  value: string;
  label: string;
}

interface ColumnFilterDropdownProps {
  /** Column name shown as "FILTER BY {label.toUpperCase()}". */
  label: string;
  options: ColumnFilterOption[];
  /** Currently committed values (checked on open, reflected in the funnel's active state). */
  selected: string[];
  /** Called with the new value set on Clear (`[]`) or Apply (the draft). */
  onApply: (values: string[]) => void;
  /** DOM id passthrough, e.g. to anchor a page tutorial step. */
  id?: string;
}

/**
 * A funnel icon that opens a checkbox multi-select panel anchored under a table column
 * header — e.g. picking both "Trainer" and "Trainee" under Role matches either, not just
 * one. Checking boxes only updates a local draft; nothing is applied to the actual filter
 * until "Apply" is clicked, so opening the panel to look never changes results. Closing
 * via an outside click discards the draft, matching a standard filter-menu convention.
 *
 * Open/dismiss/positioning come from the shared useFilterPanel hook, so this and
 * ColumnFilterPopover (the free-form variant) can't drift apart on that behaviour. The
 * panel renders through a portal into `document.body` — anchored in place, not in the
 * table's DOM subtree, so the table container's `overflow-y: hidden` can't clip it.
 */
const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({ label, options, selected, onApply, id }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string[]>(selected);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { open, setOpen, position } = useFilterPanel(anchorRef, panelRef);

  const toggleOpen = () => {
    if (!open) setDraft(selected);
    setOpen(!open);
  };

  const toggleDraftValue = (value: string) => {
    setDraft(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  const handleClear = () => {
    onApply([]);
    setDraft([]);
    setOpen(false);
  };

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  const isActive = selected.length > 0;

  return (
    <div id={id} className="column-filter" ref={anchorRef}>
      <button
        type="button"
        className={`column-filter__btn${isActive ? ' column-filter__btn--active' : ''}`}
        onClick={toggleOpen}
        aria-label={t('users.filterBy', { label })}
      >
        <ListFilter size={13} />
      </button>

      {open && position && createPortal(
        <div
          className="column-filter__panel"
          ref={panelRef}
          style={{ position: 'fixed', top: position.top, left: position.left }}
        >
          <div className="column-filter__title">{t('users.filterBy', { label })}</div>
          <div className="column-filter__options">
            {options.map(opt => (
              <label key={opt.value} className="column-filter__option">
                <input
                  type="checkbox"
                  checked={draft.includes(opt.value)}
                  onChange={() => toggleDraftValue(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <div className="column-filter__footer">
            <button type="button" className="column-filter__clear" onClick={handleClear}>{t('common.clear')}</button>
            <button type="button" className="column-filter__apply" onClick={handleApply}>{t('users.apply')}</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColumnFilterDropdown;
